import React, { useState } from 'react'
import { Keyboard, Platform, TouchableWithoutFeedback } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { BusinessFields, BusinessTerms } from '@vatom/sdk/core'
import {
  promptSelector,
  useBusiness,
  useBusinessForms,
  useBusinessTerms,
  useSDK,
  valuesSelector
} from '@vatom/sdk/react'
import { theme } from '@vatom/wombo'
import { observer } from 'mobx-react-lite'
import {
  Actionsheet,
  Box,
  Button,
  Checkbox,
  KeyboardAvoidingView,
  Pressable,
  ScrollView,
  Text
} from 'native-base'

import { useBusinessTheme } from '../../hooks/useBusinessTheme'
import { useRemoveBodyScroll } from '../../hooks/useRemoveBodyScroll'
import { AppNavigation, AppRoutes } from '../../navigators'
import { useMaxWith } from '../WebWrapper'

import Field from './Field'
import { TermsItem } from './Term'
import { TermsAndConditionsProps } from './Types'

const isSafari = Platform.select({
  web: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  native: false
})

export const TermsAndConditions = observer((props: TermsAndConditionsProps) => {
  const business = useBusiness({ businessId: props.businessId })
  const businessId = business?.data?.id
  const campaignId = business?.data?.defaultCampaignId

  const businessTerms = useBusinessTerms(businessId, promptSelector)
  const businessFields = useBusinessForms(businessId, campaignId, valuesSelector)

  const fieldsRequired = businessFields.data?.fields?.filter(f => f.required)

  const isLoading = businessTerms.isLoading || businessFields.isLoading

  const isOpen = !isLoading && (businessTerms.data?.length > 0 || fieldsRequired.length > 0)

  useRemoveBodyScroll({
    enabled: isOpen
  })

  if (!business?.data) return null

  const refetchAll = () => {
    businessTerms.refetch()
    businessFields.refetch()
  }

  return isOpen ? (
    <BusinessTermsAndConditions
      initialValues={businessFields.data.values}
      businessFields={businessFields.data.fields}
      businessTerms={businessTerms.data}
      businessId={businessId}
      campaignId={campaignId}
      refetchAll={refetchAll}
    />
  ) : null
})

export type FieldsValidationsErrors = {
  [key: BusinessFields['name']]: {
    message: string
  }
}

type TFieldsValues = Record<string, BusinessFields['value']>

type TermsAndConditionsActionSheetProps = {
  initialValues: TFieldsValues
  businessFields: BusinessFields[]
  businessTerms: BusinessTerms[]
  businessId?: string
  campaignId?: string
  refetchAll: () => void
}

const BusinessTermsAndConditions = (props: TermsAndConditionsActionSheetProps) => {
  const { initialValues, businessId, businessFields, businessTerms, campaignId, refetchAll } = props
  const sdk = useSDK()
  const maxWidth = useMaxWith()

  const { brandTheme } = useBusinessTheme()

  const [fieldsErrors, setFieldsErrors] = useState<FieldsValidationsErrors>({})
  const [termsError, setTermsError] = useState<string[]>([])

  const [termsDecisions, setTermsDecisions] = useState<string[]>([])
  const [fieldValues, setFieldValues] = useState<TFieldsValues>(initialValues)
  const [open, setOpen] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const navigation = useNavigation<AppNavigation>()

  const onChangeField = (name: string, value: string) =>
    setFieldValues(v => ({ ...v, [name]: value }))

  const cancel = () => {
    setOpen(false)
    navigation.navigate(AppRoutes.home)
  }

  const decideTerms = async () => {
    try {
      setSubmitting(true)
      setFieldsErrors({})

      const missingRequiredTerms = businessTerms.reduce<string[]>((acc, term) => {
        if (term.required && !termsDecisions.includes(term.name)) {
          acc.push(term.name)
          return acc
        }
        return acc
      }, [])
      setTermsError(missingRequiredTerms)

      const formRes = await sdk.vatomIncApi.decideBusinessForms(fieldValues, campaignId, businessId)
      if (formRes?.status === 400) {
        if (formRes.data.errors) {
          setFieldsErrors(formRes.data.errors)
          return
        }
      } else {
        setFieldsErrors({})
      }

      if (missingRequiredTerms.length === 0) {
        for (const term of businessTerms) {
          const t = termsDecisions.find(t => t === term.name)
          await sdk.vatomIncApi.decideBusinessTerms(term.name, !!t ?? false)
        }
      }

      refetchAll()
      setSubmitting(false)
    } catch (error) {
      setFieldsErrors({
        [businessFields[0].name]: {
          message: 'Unknown server error'
        }
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Actionsheet
      isOpen={open}
      onClose={cancel}
      style={{ maxWidth: maxWidth, margin: 'auto' }}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      <Actionsheet.Content pt="20px" pb="16px" px="16px">
        <KeyboardAvoidingView keyboardVerticalOffset={100} behavior={'position'} w="100%">
          <TouchableWithoutFeedback
            style={{ height: '100%' }}
            onPress={Keyboard.dismiss}
            accessible={false}
          >
            <ScrollView
              w="100%"
              h="lg"
              showsVerticalScrollIndicator={false}
              paddingBottom={isSafari ? 20 : 0}
            >
              <Text
                w="100%"
                fontFamily="Inter-SemiBold"
                fontSize={25}
                fontWeight="600"
                textAlign="left"
                mb={17}
                _light={{
                  color: brandTheme?.text ?? theme.colors.textLightMode[900]
                }}
                _dark={{
                  color: brandTheme?.text ?? theme.colors.textDarkMode[100]
                }}
              >
                Complete your profile
              </Text>
              <Text
                w="100%"
                fontFamily="Inter-Regular"
                fontSize={16}
                textAlign="left"
                _light={{
                  color: theme.colors.textLightMode[600]
                }}
                _dark={{
                  color: theme.colors.textDarkMode[300]
                }}
              >
                You are almost done! To secure your account please provide additional information
                before continuing.
              </Text>
              <Box>
                {businessFields?.map((field, key) => {
                  return (
                    <Field
                      key={key}
                      field={field}
                      error={fieldsErrors[field.name]}
                      value={fieldValues[field.name] || ''}
                      onChange={onChangeField}
                    />
                  )
                })}
              </Box>
              <Checkbox.Group
                mt={5}
                accessibilityLabel="accept terms"
                onChange={setTermsDecisions}
                accessibilityHint="accept terms"
              >
                {businessTerms?.map((terms, key) => (
                  <TermsItem
                    key={key}
                    BusinessTerms={terms}
                    checkedErr={termsError.includes(terms.name)}
                  />
                ))}
              </Checkbox.Group>

              <Box mt="10" flexDirection="row" mb="10">
                <Pressable
                  accessibilityRole="button"
                  onPress={() => cancel()}
                  justifyContent="center"
                  flex={1}
                  borderWidth={1}
                  rounded="sm"
                  mr={1}
                  _light={{
                    borderColor: theme.colors.textLightMode[300]
                  }}
                  _dark={{
                    borderColor: theme.colors.textDarkMode[600]
                  }}
                >
                  <Text fontFamily="Inter-Regular" alignSelf="center" py={2} fontSize="18px">
                    Cancel
                  </Text>
                </Pressable>
                <Button
                  accessibilityRole="button"
                  flex={1}
                  py={4}
                  rounded="sm"
                  onPress={decideTerms}
                  justifyContent="center"
                  disabled={submitting}
                  opacity={submitting ? 0.5 : 1}
                  backgroundColor={brandTheme?.primary ?? theme.colors.brand[500]}
                >
                  <Text
                    w="100%"
                    textAlign="center"
                    fontFamily="Inter-Regular"
                    alignSelf="center"
                    fontSize="18px"
                    color="white"
                    _light={{
                      color: brandTheme?.primaryText ?? 'white'
                    }}
                    _dark={{
                      color: brandTheme?.primaryText ?? theme.colors.textDarkMode[100]
                    }}
                  >
                    Accept
                  </Text>
                </Button>
                {/* <GradientButton
                  flex={1}
                  onPress={decideTerms}
                  justifyContent="center"
                  disabled={submitting}
                  opacity={submitting ? 0.5 : 1}
                >
                  <Text
                    w="100%"
                    textAlign="center"
                    fontFamily="Inter-Regular"
                    alignSelf="center"
                    py={2}
                    fontSize="18px"
                    color="white"
                  >
                    Accept
                  </Text>
                </GradientButton> */}
              </Box>
            </ScrollView>
          </TouchableWithoutFeedback>
        </KeyboardAvoidingView>
      </Actionsheet.Content>
    </Actionsheet>
  )
}
